<template>
  <div class="weekly-quantities-template-tab flex flex-col gap-4">
    <!-- Parámetros de la vista semanal -->
    <div
      class="inline-flex items-center gap-3 self-start rounded border bg-white px-3 py-2 flex-wrap"
      style="border-color: #d4dae3"
    >
      <label class="fs-11 font-semibold text-gray-600">{{ $t('current_week') }}</label>
      <span
        class="rounded border px-2 py-1 text-right fs-11 font-semibold tabular-nums"
        :style="{ borderColor: '#e2e8f0', backgroundColor: '#f8fafc', color: COL }"
        :title="weekRangeLabel"
      >{{ semanaActual }}</span>
      <span class="w-px self-stretch" style="background-color: #e2e8f0"></span>

      <label class="fs-11 font-semibold text-gray-600">{{ $t('previous_weeks') }}</label>
      <input
        type="number"
        min="0"
        max="12"
        step="1"
        :value="numPrev"
        @change="onNumPrevChange"
        class="w-16 rounded border px-2 py-1 text-right fs-11 font-semibold tabular-nums"
        :style="{ borderColor: '#c7d2e3', color: COL }"
      />
      <span class="w-px self-stretch" style="background-color: #e2e8f0"></span>

      <label class="fs-11 font-semibold text-gray-600">{{ $t('available_hours_per_person_week') }}</label>
      <span
        class="rounded border px-2 py-1 text-right fs-11 font-bold tabular-nums"
        :style="{ borderColor: '#e2e8f0', backgroundColor: '#f8fafc', color: COL }"
        :title="$t('available_hours_per_person_week_hint')"
      >{{ fmt(hhSemana) }}</span>
      <span class="fs-10 text-gray-400">{{ $t('available_hours_per_person_week_hint') }}</span>
      <span class="w-px self-stretch" style="background-color: #e2e8f0"></span>

      <label class="fs-11 font-semibold text-gray-600">{{ $t('valid_month') }}</label>
      <select
        :value="mesVigente"
        @change="selectMes(Number($event.target.value))"
        class="rounded border px-2 py-1 fs-11 font-semibold"
        :style="{ borderColor: '#c7d2e3', color: COL }"
      >
        <option v-for="(m, i) in monthLabels" :key="i" :value="i">{{ m }}</option>
      </select>
      <span class="fs-10 text-gray-400">{{ $t('weeks_in_month_for_ftd', { count: weeksInMonth }) }}</span>
    </div>

    <!-- Tabla semanal: headers siempre visibles; loading / vacío van en el cuerpo -->
    <div class="rounded border bg-white overflow-x-auto" style="border-color: #d4dae3">
      <table class="w-full border-collapse" style="min-width: 900px">
        <thead>
          <!-- Fila 1: bloques -->
          <tr>
            <th
              rowspan="2"
              class="px-3 py-1.5 text-left fs-10 font-semibold sticky-left"
              :style="{ backgroundColor: GREEN_D, color: '#fff', borderRight: '1px solid #16543a', minWidth: '200px', zIndex: 2 }"
            >{{ $t('levels') }}</th>
            <th
              rowspan="2"
              class="px-2 py-1.5 text-center fs-10 font-semibold"
              :style="{ backgroundColor: GREEN_D, color: '#fff', borderRight: '1px solid #16543a' }"
            >{{ $t('unit_abbr') }}.</th>
            <th
              colspan="1"
              class="px-2 py-1.5 text-center fs-10 font-bold uppercase tracking-wide"
              style="background-color: #2e7d52; color: #fff; border-right: 2px solid #16543a"
            >{{ $t('budget_indexes') }}</th>

            <template v-for="w in weeks">
              <th
                v-if="isOpen(w)"
                :key="'wk-open-' + w"
                colspan="8"
                @click="w !== semanaActual ? togglePrev(w) : null"
                class="px-2 py-1.5 text-center fs-10 font-bold uppercase tracking-wide"
                :class="{ 'cursor-pointer': w !== semanaActual }"
                :style="{ backgroundColor: w === semanaActual ? '#0d5f59' : '#3b8577', color: '#fff', borderRight: '2px solid #094744' }"
                :title="w !== semanaActual ? $t('collapse_week') : null"
              >
                {{ $t('week_label', { week: w }) }}
                <span
                  v-if="w === semanaActual"
                  class="ml-1.5 fs-8 px-1 py-0.5 rounded font-semibold"
                  style="background-color: #facc15; color: #374151"
                >{{ $t('week_current_badge') }}</span>
              </th>
              <th
                v-else
                :key="'wk-collapsed-' + w"
                rowspan="2"
                @click="togglePrev(w)"
                class="cursor-pointer px-0.5 py-1 fs-9 font-bold"
                style="background-color: #7ba892; color: #fff; border-right: 1px solid #5c8a75; width: 24px; max-width: 24px"
                :title="$t('expand_week_to_consult')"
              >
                <span class="week-vertical">{{ $t('week_short', { week: w }) }}</span>
              </th>
            </template>
          </tr>
          <!-- Fila 2: columnas Índices presupuesto -->
          <tr>
            <sem-head-cell
              :label="$t('adjusted_man_hour_index')"
              :tip="$t('adjusted_man_hour_index_tip')"
              :fuente="showFuente ? 'manual' : null"
            />
            <template v-for="w in openWeeks">
              <sem-head-cell
                :key="w + '-ef'"
                :label="$t('workforce')"
                :tip="$t('workforce_tip')"
                :fuente="showFuente ? 'manual' : null"
              />
              <sem-head-cell
                :key="w + '-plan'"
                :label="$t('ftd_planned')"
                :tip="$t('ftd_planned_tip')"
                :fuente="showFuente ? 'calc' : null"
                :from-tab="showFuente ? $t('weekly_qty_tab_consolidated') : null"
              />
              <sem-head-cell
                :key="w + '-prog'"
                :label="$t('ftd_programmed')"
                :tip="$t('ftd_programmed_tip')"
                :fuente="showFuente ? 'manual' : null"
              />
              <sem-head-cell
                :key="w + '-real'"
                :label="$t('ftd_actual')"
                :tip="$t('ftd_actual_tip')"
                :fuente="showFuente ? 'import' : null"
                :fuente-tip="$t('ftd_actual_fuente_tip')"
              />
              <sem-head-cell
                :key="w + '-hhplan'"
                :label="$t('mh_planned')"
                :tip="$t('mh_planned_tip')"
                :fuente="showFuente ? 'calc' : null"
              />
              <sem-head-cell
                :key="w + '-hhdisp'"
                :label="$t('mh_available')"
                :tip="$t('mh_available_tip')"
                :fuente="showFuente ? 'calc' : null"
                :from-tab="showFuente ? $t('weekly_qty_tab_premises') : null"
              />
              <sem-head-cell
                :key="w + '-hhprog'"
                :label="$t('mh_scheduled')"
                :tip="$t('mh_scheduled_tip')"
                :fuente="showFuente ? 'calc' : null"
              />
              <sem-head-cell
                :key="w + '-hhreal'"
                :label="$t('mh_real')"
                :tip="$t('mh_real_tip')"
                :fuente="showFuente ? 'import' : null"
                :fuente-tip="$t('mh_real_fuente_tip')"
              />
            </template>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td
              :colspan="tableColSpan"
              class="px-4 py-8 text-center text-gray-400 fs-12"
            >
              {{ $t('loading') }}...
            </td>
          </tr>
          <tr v-else-if="isEmpty">
            <td
              :colspan="tableColSpan"
              class="px-4 py-8 text-center text-gray-400 fs-12"
            >
              {{ $t('no_results') }}
            </td>
          </tr>
          <weekly-quantities-tree-rows
            v-else
            :project-id="projectId"
            :nodes="roots"
            :depth="0"
            :expanded-ids="expandedIds"
            :weeks="weeks"
            :semana-actual="semanaActual"
            :open-prev="openPrev"
            :calc="calc"
            :hh-semana="hhSemana"
            :mes-inicio="mesInicio"
            :project-weeks="projectWeeks"
            :week-start-date="weekStartDate"
            :week-end-date="weekEndDate"
            :weekly-api-params="weeklyApiParams"
            @toggle="$emit('toggle', $event)"
            @children-loaded="$emit('children-loaded', $event)"
          />
        </tbody>
        <tfoot v-if="!loading && !isEmpty">
          <tr style="background-color: #12483a">
            <td class="px-3 py-2 fs-11 font-bold uppercase sticky-left" style="color: #fff; background-color: #12483a; z-index: 1">{{ $t('grand_total') }}</td>
            <td></td>
            <td colspan="1" style="border-right: 2px solid #0d3a2f"></td>
            <template v-for="w in weeks">
              <td v-if="!isOpen(w)" :key="'foot-col-' + w" style="background-color: #1a5747"></td>
              <template v-else>
                <td class="foot-num" :key="'foot-ef-' + w" :style="footStyle(footTotals(w).ef, false)">{{ fmt(footTotals(w).ef) }}</td>
                <td class="foot-num" :key="'foot-plan-' + w" :style="footStyle(null, false)">—</td>
                <td class="foot-num" :key="'foot-prog-' + w" :style="footStyle(null, false)">—</td>
                <td class="foot-num" :key="'foot-real-' + w" :style="footStyle(null, false)">—</td>
                <td class="foot-num" :key="'foot-hhplan-' + w" :style="footStyle(footTotals(w).hhPlan, false)">{{ fmt(footTotals(w).hhPlan) }}</td>
                <td class="foot-num" :key="'foot-hhdisp-' + w" :style="footStyle(footTotals(w).hhDisp, false)">{{ fmt(footTotals(w).hhDisp) }}</td>
                <td class="foot-num" :key="'foot-hhprog-' + w" :style="footStyle(footTotals(w).hhProg, false)">{{ fmt(footTotals(w).hhProg) }}</td>
                <td class="foot-num" :key="'foot-hhreal-' + w" :style="footStyle(footTotals(w).hhReal, footTotals(w).warn)">{{ fmt(footTotals(w).hhReal) }}</td>
              </template>
            </template>
          </tr>
        </tfoot>
      </table>
    </div>

    <p class="fs-10 text-gray-400 leading-relaxed">
      {{ $t('weekly_qty_footer_hint') }}
    </p>
  </div>
</template>

<script>
import { makeSemanalCalc } from './gestionHHCalc.js'
import WeeklyQuantitiesTreeRows from './weekly-quantities-tree-rows.vue'
import {
  sumRootWeekTotalsFromApi,
} from './takeoffWeeklyNodes.js'

const COL = '#0f766e'
const GREEN_D = '#1e6b43'
const ROW_D_BG = '#e4efe4'
const CUR_BG = '#f4faf7'

// Formatea hasta 5 decimales; "—" cuando el valor no es calculable.
function fmt(n) {
  if (n == null || !Number.isFinite(n)) return '—'
  return n.toLocaleString('es-ES', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 5,
  })
}

// Etiqueta de fuente del dato (manual / calculado / import takeoff).
const FUENTE_STYLE = {
  manual: { bg: '#dbeafe', fg: '#1d4ed8', labelKey: 'fuente_manual', genericKey: 'fuente_manual_generic' },
  calc: { bg: '#ede9fe', fg: '#6d28d9', labelKey: 'fuente_calc', genericKey: 'fuente_calc_generic' },
  import: { bg: '#fef3c7', fg: '#a16207', labelKey: 'fuente_import', genericKey: 'fuente_import_generic' },
}

const FuenteTag = {
  name: 'fuente-tag',
  props: {
    kind: { type: String, required: true },
    tip: { type: String, default: null },
    detail: { type: String, default: null },
    fromTab: { type: String, default: null },
  },
  computed: {
    style() {
      const st = FUENTE_STYLE[this.kind]
      return { backgroundColor: st.bg, color: st.fg }
    },
    label() {
      const st = FUENTE_STYLE[this.kind]
      const base = this.$t(st.labelKey)
      return this.fromTab ? base + ' · ' + this.fromTab : base
    },
    title() {
      const st = FUENTE_STYLE[this.kind]
      const generic = this.$t(st.genericKey)
      let title = this.tip != null ? this.tip : (this.detail ? generic + '\n' + this.detail : generic)
      if (this.fromTab) {
        title += '\n' + this.$t('data_from_tab_hint', { tab: this.fromTab })
      }
      return title
    },
  },
  render(h) {
    return h(
      'span',
      {
        class: 'sem-fuente-tag block mt-1 mx-auto rounded font-bold cursor-help',
        style: this.style,
        attrs: { title: this.title },
      },
      this.label,
    )
  },
}

const SemHeadCell = {
  name: 'sem-head-cell',
  props: {
    label: { type: String, required: true },
    tip: { type: String, default: null },
    fuente: { type: String, default: null },
    fuenteTip: { type: String, default: null },
    fromTab: { type: String, default: null },
  },
  components: { FuenteTag },
  render(h) {
    const children = [this.label]
    if (this.fuente) {
      children.push(
        h(FuenteTag, {
          props: { kind: this.fuente, tip: this.fuenteTip, detail: this.tip, fromTab: this.fromTab },
        }),
      )
    }
    return h(
      'th',
      {
        class: 'px-2 py-1.5 fs-10 font-semibold text-center whitespace-nowrap' + (this.tip ? ' cursor-help' : ''),
        style: { backgroundColor: '#0f766e', color: '#e6fffa', borderRight: '1px solid #0d5f59' },
        attrs: { title: this.tip },
      },
      children,
    )
  },
}

export default {
  name: 'weekly-quantities-template-tab',
  components: { SemHeadCell, FuenteTag, WeeklyQuantitiesTreeRows },
  props: {
    projectId: { type: [Number, String], default: null },
    roots: { type: Array, default: () => [] },
    nodesById: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    expandedIds: { type: Array, default: () => [] },
    showFuente: { type: Boolean, default: false },
    mesInicio: { type: String, default: '2026-07' },
    monthLabels: { type: Array, default: () => [] },
    hhSemana: { type: Number, default: 0 },
    semanaActual: { type: Number, default: 1 },
    previousWeeks: { type: Number, default: 3 },
    weekStartDate: { type: String, default: null },
    weekEndDate: { type: String, default: null },
    projectWeeks: { type: Array, default: () => [] },
    weeklyApiParams: { type: Object, default: () => ({}) },
  },
  data() {
    const parts = String(this.mesInicio).split('-').map(Number)
    const y0 = parts[0]
    const m0 = parts[1]
    const now = new Date()
    const diff = (now.getFullYear() - (y0 || now.getFullYear())) * 12 + (now.getMonth() + 1 - (m0 || 1))
    return {
      COL,
      GREEN_D,
      ROW_D_BG,
      CUR_BG,
      numPrev: Math.max(0, Math.min(12, Number(this.previousWeeks) || 0)),
      openPrev: {},
      mesVigente: Math.max(0, Math.min(this.monthLabels.length - 1, diff)),
    }
  },
  watch: {
    semanaActual() {
      this.openPrev = {}
    },
    previousWeeks(value) {
      const next = Math.max(0, Math.min(12, Number(value) || 0))
      if (next !== this.numPrev) {
        this.numPrev = next
      }
    },
  },
  computed: {
    isEmpty() {
      return !this.roots || this.roots.length === 0
    },
    weekRangeLabel() {
      if (this.weekStartDate && this.weekEndDate) {
        return this.weekStartDate + ' – ' + this.weekEndDate
      }
      return ''
    },
    stageNodes() {
      return Object.values(this.nodesById || {}).filter((node) => Number(node.rowKind) === 2)
    },
    pctFor() {
      // Solo para makeSemanalCalc (mapa de semanas del mes). Los % de FTD vienen del API.
      return () => 0
    },
    calc() {
      return makeSemanalCalc({
        mesInicio: this.mesInicio,
        monthLabelsLen: this.monthLabels.length,
        semanaActual: this.semanaActual,
        pctFor: this.pctFor,
        vals: {},
        fatorK: {},
        hhSemana: this.hhSemana,
      })
    },
    weeksInMonth() {
      return this.calc.weeksIn(this.mesVigente)
    },
    weeks() {
      const calc = this.calc
      const n = Math.max(0, Math.min(12, this.numPrev))
      const set = {}
      const order = []
      const add = (w) => {
        if (!(w in set)) {
          set[w] = true
          order.push(w)
        }
      }
      for (let i = 0; i <= n; i++) add(this.semanaActual - n + i)
      const mesNums = calc.monthWeekNums(this.mesVigente)
      const esMesActual = mesNums.indexOf(this.semanaActual) !== -1
      for (const w of mesNums) if (w <= this.semanaActual || esMesActual) add(w)
      return order.sort((x, y) => x - y)
    },
    openWeeks() {
      return this.weeks.filter((w) => this.isOpen(w))
    },
    /** NIVELES + Ud. + índice HH + 8 cols por semana abierta / 1 por semana replegada. */
    tableColSpan() {
      return 3 + this.weeks.reduce((total, week) => total + (this.isOpen(week) ? 8 : 1), 0)
    },
  },
  methods: {
    fmt,
    isOpen(w) {
      return w === this.semanaActual || !!this.openPrev[w]
    },
    togglePrev(w) {
      this.$set(this.openPrev, w, !this.openPrev[w])
    },
    footTotals(w) {
      return sumRootWeekTotalsFromApi(this.roots, w)
    },
    footStyle(v, warn) {
      return { color: v == null ? '#5b7f72' : warn ? '#fca5a5' : '#d9f2e6' }
    },
    onNumPrevChange(e) {
      const v = Number(e.target.value)
      if (!Number.isFinite(v)) {
        return
      }
      const next = Math.max(0, Math.min(12, Math.round(v)))
      this.numPrev = next
      this.$emit('update:previous-weeks', next)
    },
    selectMes(mi) {
      this.mesVigente = mi
      const weekNums = this.calc.monthWeekNums(mi)
      const nums = weekNums.filter((w) => w < this.semanaActual)
      if (weekNums.indexOf(this.semanaActual) !== -1 || nums.length === 0) {
        this.openPrev = {}
        return
      }
      const next = Object.assign({}, this.openPrev)
      for (const w of nums) next[w] = true
      this.openPrev = next
    },
  },
}
</script>

<style scoped>
/* Tamaños de fuente arbitrarios del prototipo (Tailwind 2.2 no admite text-[10px]). */
.fs-8 {
  font-size: 8px;
}
.fs-9 {
  font-size: 9px;
}
.fs-10 {
  font-size: 10px;
}
.fs-11 {
  font-size: 11px;
}
.fs-11-5 {
  font-size: 11.5px;
}
.fs-12 {
  font-size: 12px;
}

.sticky-left {
  position: sticky;
  left: 0;
}

/* Etiqueta de semana replegada en vertical. */
.week-vertical {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  white-space: nowrap;
  display: inline-block;
}

/* Badge de fuente del dato. */
.sem-fuente-tag {
  width: fit-content;
  padding: 1px 4px;
  font-size: 8px;
  text-transform: none;
  letter-spacing: 0.025em;
}

/* Celda numérica de la tabla semanal. */
.sem-num {
  padding: 4px 8px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-size: 11px;
}

/* Input de la tabla semanal. */
.sem-input {
  min-width: 62px;
}

/* Celda numérica del pie (total general). */
.foot-num {
  padding: 8px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-size: 11px;
  font-weight: 700;
}
</style>
